import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  resetEmailConfig,
  setEmailConfig,
  setEmailError,
  setEmailLoading,
  setEmailSubmitting,
  setEmailTestRecipient,
  setEmailTesting,
  updateEmailField,
} from '../../store/slices/email/emailConfigSlice';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useLoading from '../useLoading';
import EmailConfigService from '../../services/email/EmailConfigService';
import {
  EmailConfigurationForm,
  UpdateEmailConfiguration,
} from '../../types/email/EmailTypes';

export const useEmailConfig = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { form, testEmail, isLoading, isSubmitting, isTesting, error } =
    useSelector((state: RootState) => state.emailConfig);
  const { handle } = useApiErrorHandler();
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

  const loadEmailConfig = async () => {
    try {
      dispatch(setEmailLoading(true));
      showLoadingIndicator();

      const response = await EmailConfigService.getConfiguration();

      if (response.statusCode === 200) {
        dispatch(setEmailConfig(response.data));
      } else {
        dispatch(setEmailError('No se pudo cargar la configuracion de correo.'));
        showAlert('error', 'Error', 'No se pudo obtener la configuracion de correo.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setEmailLoading(false));
      hideLoadingIndicator();
    }
  };

  const saveEmailConfig = async (data: EmailConfigurationForm) => {
    try {
      dispatch(setEmailSubmitting(true));
      showLoadingIndicator();

      const payload: UpdateEmailConfiguration = {
        provider: data.provider,
        smtpHost: data.smtpHost,
        smtpPort: data.smtpPort,
        username: data.username,
        password: data.password,
        fromName: data.fromName,
        fromEmail: data.fromEmail,
        authEnabled: data.authEnabled,
        tlsEnabled: data.tlsEnabled,
        active: data.active,
      };

      const response = await EmailConfigService.saveOrUpdate(payload);

      if (response.statusCode === 200 || response.statusCode === 201) {
        showAlert(response.type, response.title, response.message);
        dispatch(setEmailConfig(response.data));
      } else {
        showAlert('error', 'Error', 'No se pudo guardar la configuracion de correo.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setEmailSubmitting(false));
      hideLoadingIndicator();
    }
  };

  const sendTestEmail = async (email: string) => {
    if (!email.trim()) {
      showAlert('warning', 'Correo requerido', 'Ingrese un correo para enviar la prueba.');
      return;
    }

    try {
      dispatch(setEmailTesting(true));
      showLoadingIndicator();

      const response = await EmailConfigService.sendTestEmail({ email });

      if (response.statusCode === 200 || response.statusCode === 201) {
        showAlert(response.type, response.title, response.message);
      } else {
        showAlert('error', 'Error', 'No se pudo enviar el correo de prueba.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setEmailTesting(false));
      hideLoadingIndicator();
    }
  };

  const updateField = <K extends keyof EmailConfigurationForm>(
    field: K,
    value: EmailConfigurationForm[K]
  ) => {

    if (field === 'provider') {

      switch (value) {

        case 'GMAIL':
          dispatch(updateEmailField({
            field: 'provider',
            value
          }));

          dispatch(updateEmailField({
            field: 'smtpHost',
            value: 'smtp.gmail.com'
          }));

          dispatch(updateEmailField({
            field: 'smtpPort',
            value: 587
          }));

          dispatch(updateEmailField({
            field: 'authEnabled',
            value: true
          }));

          dispatch(updateEmailField({
            field: 'tlsEnabled',
            value: true
          }));

          return;

        case 'OUTLOOK':
          dispatch(updateEmailField({
            field: 'provider',
            value
          }));

          dispatch(updateEmailField({
            field: 'smtpHost',
            value: 'smtp.office365.com'
          }));

          dispatch(updateEmailField({
            field: 'smtpPort',
            value: 587
          }));

          dispatch(updateEmailField({
            field: 'authEnabled',
            value: true
          }));

          dispatch(updateEmailField({
            field: 'tlsEnabled',
            value: true
          }));

          return;

        case 'SMTP_CUSTOM':
          dispatch(updateEmailField({
            field: 'provider',
            value
          }));

          dispatch(updateEmailField({
            field: 'smtpHost',
            value: ''
          }));

          dispatch(updateEmailField({
            field: 'smtpPort',
            value: 587
          }));

          return;
      }
    }

    dispatch(updateEmailField({ field, value }));
  };

  const updateTestEmail = (email: string) => {
    dispatch(setEmailTestRecipient(email));
  };

  const resetForm = () => {
    dispatch(resetEmailConfig());
  };

  return {
    form,
    testEmail,
    isLoading,
    isSubmitting,
    isTesting,
    error,
    loadEmailConfig,
    saveEmailConfig,
    sendTestEmail,
    updateField,
    updateTestEmail,
    resetForm,
  };
};

export default useEmailConfig;
